import { cast, flow, getSnapshot, Instance, types } from 'mobx-state-tree';
import { ILogInParams } from '../../http-requests/auth/types';
import { ICallData } from '../../layouts/authorized/components/call-card/types';
import { Auth, Telephony } from '../../http-requests';
import { ChangeUserForWorker } from '../../http-requests/auth';

import { RequestStateModel } from './general-models';

const AuthModel = types.model({
  is_super: types.optional(types.boolean, false),
  is_master_super: types.optional(types.boolean, false),
  slave_control: types.optional(types.maybeNull(types.boolean), null),
  number: types.optional(types.string, ''),
});

const AuthDriveThroughModel = types.model({
  _token: types.optional(types.string, ''),
});

const PositionModel = types.model({
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
  // code: types.optional(types.string, ''),
});

const DepartmentModel = types.model({
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
});

const RightsModel = types.model({
  create: types.optional(types.boolean, false),
  delete: types.optional(types.boolean, false),
  read: types.optional(types.boolean, false),
  update: types.optional(types.boolean, false),
});

// const TabModel = types.model({
//   is_active: types.optional(types.boolean, false),
//   parent: types.optional(types.string, ''),
//   // promo_url: null
//   rights: types.optional(RightsModel, {}),
//   slug: types.optional(types.string, ''),
//   title: types.optional(types.string, ''),
//   _id: types.optional(types.string, ''),
//   _type: types.optional(types.array(types.string), []),
//   // permissions_help: null,
//   // departments: [],
// });

const PermissionModel = types
  .model({
    crud: types.optional(types.string, ''),
    title: types.optional(types.string, ''),
  })
  .views((self) => ({
    get rights() {
      return {
        create: self.crud.includes('C'),
        read: self.crud.includes('R'),
        update: self.crud.includes('U'),
        delete: self.crud.includes('D'),
      };
    },
  }));

const OnlineCashModel = types.model({
  active: types.optional(types.boolean, false),
  preference: types.optional(types.maybeNull(types.string), null),
});

const LogoModel = types.model({
  file: types.optional(types.maybeNull(types.string), null),
  filename: types.optional(types.maybeNull(types.string), null),
  name: types.optional(types.maybeNull(types.string), null),
  uuid: types.optional(types.maybeNull(types.string), null),
});

const ProviderModel = types.model({
  business_types: types.optional(types.array(types.string), []),
  // client_access: types.optional(types.boolean, false),
  inn: types.optional(types.string, ''),
  // phones: []
  str_name: types.optional(types.string, ''),
  id: types.optional(types.string, ''),
  // tabs: types.optional(types.array(TabModel), []),
  gis_online_changes: types.optional(types.maybeNull(types.boolean), null),
  online_cash: types.optional(OnlineCashModel, {}),
  url: types.optional(types.string, ''),
  logo_image: types.optional(LogoModel, {}),
});

const UserPhone = types.model({
  add: types.optional(types.maybeNull(types.string), ''),
  added_by_tenant: types.optional(types.boolean, false),
  code: types.optional(types.maybeNull(types.string), ''),
  not_actual: types.optional(types.boolean, false),
  number: types.optional(types.maybeNull(types.string), ''),
  str_number: types.optional(types.maybeNull(types.string), ''),
  phone_type: types.optional(types.string, ''), // 'cell' и другие варианты (узнать)
  id: types.optional(types.maybeNull(types.string), ''),
  telegram: types.optional(types.boolean, false),
  viber: types.optional(types.boolean, false),
  whatsapp: types.optional(types.boolean, false),
});

const TelephonyMeta = types
  .model({
    is_telephony: types.optional(types.boolean, false),
    work_numbers: types.optional(types.array(types.string), []),
  })
  .actions((self) => ({
    setMeta(telephonyMeta: ITelephonyMeta) {
      self.is_telephony = telephonyMeta.is_telephony;
      self.work_numbers.replace(telephonyMeta.work_numbers);
    },
  }));

const StatusesModel = types.model({
  logIn: types.optional(RequestStateModel, {}),
  auth_drive_through: types.optional(RequestStateModel, {}),
  user_info: types.optional(RequestStateModel, {}),
  permissions: types.optional(RequestStateModel, {}),
  telephonyInitialData: types.optional(RequestStateModel, {}),
  repairPassword: types.optional(RequestStateModel, {}),
  accounts: types.optional(RequestStateModel, {}),
  switchAccount: types.optional(RequestStateModel, {}),
});

export type PermissionsType = Instance<typeof UserStore>['permissions'];

interface ITelephonyMeta extends Instance<typeof TelephonyMeta> {}

const AccountModel = types.model({
  id: types.string,
  company_mongo_id: types.string,
  username: types.string,
});

export const UserStore = types
  .model({
    telephonyMeta: types.optional(TelephonyMeta, {}), //мета сокета телефонии
    allActiveCalls: types.optional(types.array(types.frozen<ICallData>()), []), //активные звонки при инициализации
    auth: types.optional(AuthModel, {}),
    business_types: types.optional(types.array(types.string), []), //TODO: может исчезнет
    department: types.optional(DepartmentModel, {}),
    email: types.optional(types.maybeNull(types.string), ''),
    phones: types.optional(types.array(UserPhone), []),
    position: types.optional(PositionModel, {}),
    provider: types.optional(ProviderModel, {}),
    short_name: types.optional(types.string, ''),
    str_name: types.optional(types.string, ''),
    // tabs: types.optional(types.array(TabModel), []),
    id: types.optional(types.string, ''),
    type: types.optional(types.string, ''),
    auth_drive_through: types.optional(AuthDriveThroughModel, {}),
    permissions: types.optional(types.map(PermissionModel), {}),
    statuses: types.optional(StatusesModel, {}),
    accounts: types.optional(types.array(AccountModel), []),
  })
  .views((self) => ({
    get isSuperUser(): boolean {
      return self.auth.is_super || self.auth.is_master_super;
    },
    get hasTelephony(): boolean {
      /* определяет есть ли телефония */
      return (
        self.telephonyMeta.is_telephony &&
        !!self.telephonyMeta.work_numbers?.length
      );
    },
    hasItsNumber(phoneNumber: string) {
      return self.telephonyMeta.work_numbers.includes(phoneNumber);
    },
    get providerId(): string {
      return self.provider.id;
    },
    get isLoading() {
      return (
        self.statuses.user_info.pending || self.statuses.permissions.pending
      );
    },
    get isError() {
      return self.statuses.user_info.error || self.statuses.permissions.error;
    },
    // Проверка business_types организации на вид деятельности "Управление домами"
    // на момент написания комментария требуется исключительно для Справочника алгоритмов
    get isProviderUdo() {
      return self.provider.business_types.some((type) =>
        ['udo', 'ads', 'cс'].includes(type)
      );
    },
  }))
  .actions((self) => ({
    getTelephonyInitialData: flow(function* () {
      try {
        self.statuses.telephonyInitialData.loadingStates();

        const response: {
          data: {
            all_active_calls: ICallData[];
          };
          meta: {
            group_name: string;
            provider: string;
            telephony: ITelephonyMeta;
            user: string;
          };
        } = yield Telephony.GetTelephonyInitialData();

        self.allActiveCalls.replace(response.data.all_active_calls);
        self.telephonyMeta.setMeta(response.meta.telephony);

        self.statuses.telephonyInitialData.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.telephonyInitialData.errorStates();
      } finally {
        self.statuses.telephonyInitialData.finallyStates();
      }
    }),
    getPermissions: flow(function* () {
      try {
        self.statuses.permissions.loadingStates();
        const response = yield Auth.GetPermissions();
        self.permissions = response.permissions;
        self.statuses.permissions.successStates();

        return getSnapshot(self);
      } catch (error) {
        console.error(error);
        self.statuses.permissions.errorStates();

        throw error;
      } finally {
        self.statuses.permissions.finallyStates();
      }
    }),
    getUserInfo: flow(function* () {
      try {
        self.statuses.user_info.loadingStates();
        const response = yield Auth.GetUserInfo();

        self.auth = response.auth;
        self.business_types = response?.business_types;
        self.department = response.department;
        self.email = response?.email;
        self.phones = response?.phones;
        self.position = response.position;
        self.provider = response.provider;
        self.short_name = response.short_name;
        self.str_name = response.str_name;
        self.id = response.id;
        self.type = response.type;

        self.statuses.user_info.successStates();

        return getSnapshot(self);
      } catch (error) {
        console.error(error);
        self.statuses.user_info.errorStates();

        throw error;
      } finally {
        self.statuses.user_info.finallyStates();
      }
    }),

    logIn: flow(function* (params: ILogInParams) {
      try {
        self.statuses.logIn.loadingStates();
        const response = yield Auth.GatewayLogIn(params);
        self.statuses.logIn.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.logIn.errorStates();

        throw e;
      } finally {
        self.statuses.logIn.finallyStates();
      }
    }),
    logOut: flow(function* () {
      try {
        const response = yield Auth.GatewayLogOut();
        window.location.reload();

        return response;
      } catch (e) {
        console.error(e);
      }
    }),
    repairPassword: flow(function* (login: string) {
      try {
        self.statuses.repairPassword.loadingStates();
        yield Auth.ResetPassword(login);
        self.statuses.repairPassword.successStates();
      } catch (error) {
        self.statuses.repairPassword.errorStates();
        console.error(error);

        return Promise.reject(error);
      } finally {
        self.statuses.repairPassword.finallyStates();
      }
    }),
    postAuthThroughKey: flow(function* (params: any) {
      self.statuses.auth_drive_through.loadingStates();

      try {
        const response = yield Auth.PostAuthThroughKey(params);
        self.statuses.auth_drive_through.successStates();

        return response;
      } catch (e) {
        self.statuses.auth_drive_through.errorStates();
        console.error(e);
      } finally {
        self.statuses.auth_drive_through.finallyStates();
      }
    }),
    getWorkerAccounts: flow(function* () {
      try {
        self.statuses.accounts.loadingStates();
        self.accounts = yield Auth.GetWorkerAccounts();
        self.statuses.accounts.successStates();
      } catch (e) {
        self.statuses.accounts.errorStates();
        console.error(e);
      } finally {
        self.statuses.accounts.finallyStates();
      }
    }),
    switchAccountForWorker: flow(function* (userId: string) {
      try {
        self.statuses.switchAccount.loadingStates();
        yield ChangeUserForWorker(userId);
        self.statuses.switchAccount.successStates();
      } catch (e) {
        self.statuses.switchAccount.errorStates();
        console.error(e);

        return Promise.reject(e);
      } finally {
        self.statuses.switchAccount.finallyStates();
      }
    }),
  }));
export interface IUserStore extends Instance<typeof UserStore> {}
